import React from 'react';
import { Loader2, Zap, Star, Award } from 'lucide-react';
import '../styles/premium-theme.css';

// Skeleton Loader Component
export const SkeletonLoader = ({ width = '100%', height = '20px', className = '' }) => (
  <div 
    className={`cz-skeleton ${className}`}
    style={{ width, height }}
  />
);

// Card Skeleton for Dashboard
export const CardSkeleton = () => (
  <div className="cz-card cz-card-skeleton">
    <div className="cz-card-header">
      <SkeletonLoader width="80px" height="24px" />
      <SkeletonLoader width="60px" height="20px" />
    </div>
    <div className="cz-card-content">
      <SkeletonLoader width="100%" height="24px" className="mb-3" />
      <SkeletonLoader width="100%" height="16px" className="mb-2" />
      <SkeletonLoader width="80%" height="16px" className="mb-4" />
      <SkeletonLoader width="120px" height="20px" />
    </div>
    <div className="cz-card-footer">
      <SkeletonLoader width="80px" height="32px" />
      <div style={{ display: 'flex', gap: '8px' }}>
        <SkeletonLoader width="40px" height="32px" />
        <SkeletonLoader width="100px" height="32px" />
      </div>
    </div>
  </div>
);

// Premium Loading Spinner
export const PremiumSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  return (
    <div className={`cz-spinner ${className}`}>
      <Loader2 className={`${sizeClasses[size]} animate-spin text-gold`} />
    </div>
  );
};

// Full Page Loading
export const FullPageLoader = ({ message = 'Loading...' }) => (
  <div className="cz-full-page-loader">
    <div className="cz-loader-content">
      <div className="cz-loader-animation">
        <div className="cz-loader-ring">
          <Zap className="w-8 h-8" />
        </div>
        <div className="cz-loader-particles">
          {[...Array(6)].map((_, i) => (
            <div 
              key={i} 
              className="cz-loader-particle"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>
      <h3 className="cz-loader-title">Crezar</h3>
      <p className="cz-loader-message">{message}</p>
    </div>
  </div>
);

// Progress Bar Component
export const ProgressBar = ({ progress = 0, className = '', showPercentage = false }) => (
  <div className={`cz-progress-container ${className}`}>
    <div className="cz-progress-bar">
      <div 
        className="cz-progress-fill"
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </div>
    {showPercentage && (
      <span className="cz-progress-text">{Math.round(progress)}%</span>
    )}
  </div>
);

// Button Loading State
export const ButtonLoader = ({ children, loading = false, disabled = false, ...props }) => (
  <button 
    {...props}
    disabled={disabled || loading}
    className={`${props.className || ''} ${loading ? 'cz-btn-loading' : ''}`}
  >
    {loading ? (
      <>
        <PremiumSpinner size="sm" />
        <span>Loading...</span>
      </>
    ) : (
      children
    )}
  </button>
);

// Pulse Animation for Live Elements
export const PulseIndicator = ({ className = '', children }) => (
  <div className={`cz-pulse-indicator ${className}`}>
    {children}
    <div className="cz-pulse-ring" />
  </div>
);

// Floating Action Button with Animation
export const FloatingActionButton = ({ icon: Icon, onClick, className = '', tooltip }) => (
  <div className="cz-fab-container">
    <button 
      className={`cz-fab ${className}`}
      onClick={onClick}
      title={tooltip}
    >
      <Icon className="w-6 h-6" />
      <div className="cz-fab-ripple" />
    </button>
  </div>
);

// Success/Error Toast Animation
export const AnimatedToast = ({ type = 'success', message, onClose }) => {
  const icons = {
    success: Star,
    error: Zap,
    warning: Award
  };
  
  const Icon = icons[type];

  return (
    <div className={`cz-toast cz-toast--${type} cz-animate-slide-in-right`}>
      <div className="cz-toast-icon">
        <Icon className="w-5 h-5" />
      </div>
      <div className="cz-toast-content">
        <p className="cz-toast-message">{message}</p>
      </div>
      <button className="cz-toast-close" onClick={onClose}>
        ×
      </button>
    </div>
  );
};

// Loading Overlay for Components
export const LoadingOverlay = ({ loading = false, children, message = 'Loading...' }) => (
  <div className="cz-loading-overlay-container">
    {children}
    {loading && (
      <div className="cz-loading-overlay">
        <div className="cz-loading-overlay-content">
          <PremiumSpinner size="lg" />
          <p className="cz-loading-overlay-message">{message}</p>
        </div>
      </div>
    )}
  </div>
);

export default {
  SkeletonLoader,
  CardSkeleton,
  PremiumSpinner,
  FullPageLoader,
  ProgressBar,
  ButtonLoader,
  PulseIndicator,
  FloatingActionButton,
  AnimatedToast,
  LoadingOverlay
};
